import { useEffect, useState } from "react";
import { Outlet } from "fusion-plugin-react-router";
import { Helmet } from "fusion-plugin-react-helmet-async";
import {
  getUserIndicator,
  getCustomerObjectId,
  getForeName,
} from "../../../../shared/utils/userSessionUtils";
import Footer from "../../../../shared/components/footer";
import Navbar from "../../../../shared/components/navbar";
import PageContent from "../../../../shared/components/PageContent";
import QuoteStepLoader from "../../../../shared/components/QuoteStepLoader";
import {
  getCustomerPolicies,
  getCustomerQuotes,
} from "../../../../shared/services/insuranceService";
import useAppStateStore from "../../../../shared/store/AppStateStore";
import {
  clearAllStores,
  clearAllStoresPreservingPortalCustomerData,
  clearSupportStore,
} from "../../../../shared/utils/storeUtils";

const PORTAL_CUSTOMER_DATA_TTL = 5 * 60 * 1000;

const getPortalCustomerDataKey = (indicator, customerObjectId) =>
  `${indicator}:${customerObjectId}`;

const hasFreshPortalCustomerData = (indicator, customerObjectId) => {
  if (indicator !== "Policy" && indicator !== "Quote") return false;

  const state = useAppStateStore.getState();
  const fetchedAt = state.portalCustomerDataFetchedAt;
  const isFresh =
    typeof fetchedAt === "number" &&
    Date.now() - fetchedAt >= 0 &&
    Date.now() - fetchedAt < PORTAL_CUSTOMER_DATA_TTL;

  return (
    isFresh &&
    state.portalCustomerDataKey ===
      getPortalCustomerDataKey(indicator, customerObjectId) &&
    (indicator === "Policy"
      ? state.customerPolicies != null
      : state.customerQuotes != null)
  );
};

function LandingLayout() {
  const STATE = useAppStateStore();
  const indicator = getUserIndicator();
  const customerObjectId = getCustomerObjectId();
  const foreName = getForeName();
  const [loading, setLoading] = useState(
    () => !hasFreshPortalCustomerData(indicator, customerObjectId)
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    clearSupportStore();

    const hasFreshData = hasFreshPortalCustomerData(indicator, customerObjectId);
    if (hasFreshData) {
      clearAllStoresPreservingPortalCustomerData();
      setLoading(false);
      setError(null);
      return () => {
        isMounted = false;
      };
    }

    clearAllStores();

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (indicator === "Policy") {
          const policies = await getCustomerPolicies(customerObjectId);
          if (isMounted) {
            STATE.setCustomerPolicies(policies);
            STATE.setPortalCustomerDataFetchedAt(Date.now());
            STATE.setPortalCustomerDataKey(
              getPortalCustomerDataKey(indicator, customerObjectId)
            );
          }
        } else if (indicator === "Quote") {
          const quotes = await getCustomerQuotes(customerObjectId);
          if (isMounted) {
            STATE.setCustomerQuotes(quotes);
            STATE.setPortalCustomerDataFetchedAt(Date.now());
            STATE.setPortalCustomerDataKey(
              getPortalCustomerDataKey(indicator, customerObjectId)
            );
          }
        } else {
          STATE.setCustomerPolicies(null);
          STATE.setCustomerQuotes(null);
          STATE.setPortalCustomerDataFetchedAt(null);
          STATE.setPortalCustomerDataKey(null);
        }
      } catch (err) {
        if (isMounted) setError(err.message || "Failed to fetch data");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [indicator, customerObjectId]);

  const outletContext = { indicator, foreName, error };

  return (
    <>
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
      </Helmet>
      <Navbar />
      <PageContent>
        {loading ? (
          <QuoteStepLoader
            title="Driver Insurance Services"
            description="Your policy information is on its way."
            message="We're retrieving your policy details."
          />
        ) : (
          <Outlet context={outletContext} />
        )}
      </PageContent>
      <Footer />
    </>
  );
}

export default LandingLayout;
